/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { v2, errorToObject } from "@prftesp/cle-logger";
import { APIGateways } from "../../../utility/APIGateways";
import errorCodes from "../../../utility/errorCodes";
import { getAuthHeader } from "../../../utility/auth";

const COMPONENT_NAME = "adminCCPServices/emailService";

/**
 * Gets all emails for contact
 */
export const getContactEmails = async (inquiryId, lastEmailDate) => {
	try {
		const response = await axios.get(`${APIGateways.emailEndpoint}/${inquiryId}`, {
			headers: await getAuthHeader(),
			params: {
				lastEmailDate,
			},
		});
		return response.data.emails;
	} catch (error) {
		v2.error("Error while getting emails", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_EMAILS_ERROR,
		});

		throw error;
	}
};

/**
 * Sends an outbound email for contact
 * @param {string} inquiryId inquiry ID for this email conversation
 * @param {string} content content of the email to be sent
 * @param {string[]} attachmentNames array of attachment names to be included in the email
 */
export const sendEmail = async (inquiryId, content, attachmentNames) => {
	try {
		const response = await axios.post(
			`${APIGateways.emailEndpoint}/${inquiryId}`,
			{ content, attachmentNames },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error("Error while sending email", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.SEND_EMAIL_ERROR,
		});

		throw error;
	}
};

/**
 * Ends the email interaction
 * @param {string} inquiryId inquiry ID for this email conversation
 * @param {string} lastEmailDate date of the latest email
 */
export const endEmailInteraction = async (inquiryId, lastEmailDate) => {
	try {
		const response = await axios.post(
			`${APIGateways.emailEndpoint}/end`,
			{ inquiryId, lastEmailDate },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error("Error while ending email interaction", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.END_EMAIL_INTERACTION_ERROR,
		});

		throw error;
	}
};

/**
 * Updates the email interaction
 * @param {string} inquiryId inquiry ID for this email conversation
 * @param {string} lastEmailDate date of the latest email
 * @param {string} contactId contact ID
 */
export const updateEmailInteraction = async (inquiryId, lastEmailDate, contactId) => {
	try {
		const response = await axios.post(
			`${APIGateways.emailEndpoint}/update`,
			{ inquiryId, lastEmailDate, contactId },
			{
				headers: await getAuthHeader(),
			},
		);
		return response.data.result;
	} catch (error) {
		v2.error("Error while updating email interaction", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.UPDATE_EMAIL_INTERACTION_ERROR,
		});

		throw error;
	}
};

/**
 * Get an email attachment
 * @param {string} inquiryId inquiry ID related to attachment
 * @param {string} emailDate date of the email for this attachment
 * @param {string} fileName name of the attachment to be retrieved
 * @returns {Promise<string>} a promise resolving in a signed url to the attachment
 */
export const getEmailAttachment = async (inquiryId, emailDate, fileName) => {
	try {
		const response = await axios.get(`${APIGateways.emailEndpoint}/${inquiryId}/attachment`, {
			headers: await getAuthHeader(),
			params: { emailDate, fileName },
		});
		return response.data;
	} catch (error) {
		v2.error("Error while getting email attachment", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_EMAIL_ATTACHMENT_ERROR,
		});

		throw error;
	}
};

/**
 * Get a pre-signed url generated to allow a file to be uploaded
 * @param {string} inquiryId inquiry ID related to attachment
 * @param {string} fileName name of the attachment to be retrieved
 */
export const getAttachmentUploadUrl = async (inquiryId, fileName) => {
	try {
		const response = await axios.get(`${APIGateways.emailEndpoint}/${inquiryId}/attachment/upload`, {
			headers: await getAuthHeader(),
			params: { fileName },
		});
		return response.data;
	} catch (error) {
		v2.error("Error while getting attachment upload url", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_ATTACHMENT_UPLOAD_URL_ERROR,
		});

		throw error;
	}
};

/**
 * Uploads an email attachment using a pre-signed url
 * @param {string} signedUrl the pre-signed url generated for uploading this attachment
 * @param {File} attachment file to be uploaded
 */
export const uploadAttachment = async (signedUrl, attachment) => {
	try {
		const formData = new FormData();
		formData.append("Content-Type", attachment.type);
		Object.entries(signedUrl.fields).forEach(([k, v]) => formData.append(k, v));
		formData.append("file", attachment);

		const response = await axios.post(signedUrl.url, formData);
		return response.data;
	} catch (error) {
		v2.error("Error while uploading email attachment", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.UPLOAD_EMAIL_ATTACHMENT_ERROR,
		});

		throw error;
	}
};
